"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslations } from "next-intl";
import { EmptyState } from "@/components/ui/empty-state";
import AuthModal from "@/components/ui/auth-modal";
import { useOverlay } from "@/components/context/overlay-context";
import { CATEGORY_AXIS_TO_SLUG } from "@/lib/feed/category-slug";
import { CATEGORY_PHOTO_IDS } from "@/components/feed/browse-categories";
import { useMediaQuery } from "@/hooks/use-media-query";
import { ItemCard } from "@/components/search/item-card";
import type { FeedFilterState } from "@/hooks/use-paginated-feed";
import { usePaginatedFeed } from "@/hooks/use-paginated-feed";
import { useRouter } from "next/navigation";
import { AlertTriangle, Loader2 } from "lucide-react";
import { ItemCardSkeletonGrid } from "@/components/search/item-card-skeleton";
import { FeedFilters, type FeedFiltersState } from "@/components/feed/feed-filters";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { createClient } from "@/utils/supabase/client";
import { collectorKeysToFeedFiltersPartial } from "@/lib/collector-preferences-to-feed-filters";
import {
  normalizePreferenceCategories,
  type CollectorPreferenceKey,
} from "@/lib/collector-preference-keys";
import {
  buildPrefsStripCacheKey,
  parsePrefsStripCache,
  serializePrefsStripCache,
  PREFS_STRIP_CACHE_TTL_MS,
} from "@/lib/feed/prefs-strip-cache";
import {
  buildLiveStripStorageKey,
  decideInitialLiveStripState,
  decideRestoredLiveStrip,
  serializeLiveStrip,
  LIVE_STRIP_TTL_MS,
} from "@/lib/feed/live-strip-persistence";

const PAGE_LIMIT = 20;

const EMPTY_STRIP: FeedFiltersState = {
  brands: [],
  themes: [],
  categories: [],
  conditions: [],
  gradeRange: null,
  sortBy: null,
  listingCategory: null,
  cardGradeRange: null,
};

interface LiveFeedProps {
  currentUserId?: string;
  refreshSignal?: number;
}

export default function LiveFeed({ currentUserId, refreshSignal }: LiveFeedProps) {
  const tFilters = useTranslations("feedFilters");
  const tEmpty = useTranslations("emptyState");
  const { openPanel } = useOverlay();
  const [sellAuthOpen, setSellAuthOpen] = useState(false);
  const router = useRouter();
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const loadMoreArmedRef = useRef(true);
  const refreshInitializedRef = useRef(false);
  const isSm = useMediaQuery("(min-width: 640px)");
  const isLg = useMediaQuery("(min-width: 1024px)");
  const columns = isLg ? 4 : isSm ? 3 : 2;

  // Read the RESOLVED-prefs cache for the current user exactly once, during
  // first render, so the useState lazy initializers below can seed the REAL
  // strip synchronously — the usePaginatedFeed snapshot key (derived from
  // `criteria` below) is then correct from render one, so its mount scroll-
  // restore path (gated on hydrating from THAT key) actually fires for
  // logged-in users with resolved prefs. Without this, `strip` starts at
  // EMPTY_STRIP for one render and flips once `loadPrefsIntoStrip` resolves —
  // a real filters change from the hook's point of view, which intentionally
  // does NOT restore scroll (see hooks/use-paginated-feed.ts branch (2)).
  // Guarded for SSR; this component mounts client-only (behind the isDesktop
  // gate in feed-page-client.tsx) so there's no hydration mismatch — mirrors
  // the `initialSnapshotRef` pattern in usePaginatedFeed.
  // @see lib/feed/prefs-strip-cache.ts
  const initialPrefsCacheRef = useRef<Partial<FeedFiltersState> | null | undefined>(undefined);
  if (initialPrefsCacheRef.current === undefined) {
    initialPrefsCacheRef.current =
      typeof window !== "undefined" && currentUserId
        ? parsePrefsStripCache(
            window.sessionStorage.getItem(buildPrefsStripCacheKey(currentUserId)),
            Date.now(),
            PREFS_STRIP_CACHE_TTL_MS
          )
        : null;
  }

  // Persisted USER-EDIT strip (chip selections the user actually made) — takes
  // precedence over the resolved-prefs cache above (that cache reflects SERVER
  // collector preferences, not what the user clicked). Read synchronously for
  // the same reason as `initialPrefsCacheRef`: the usePaginatedFeed snapshot
  // key must be correct from render one for its mount-time scroll-restore path
  // to fire. @see lib/feed/live-strip-persistence.ts
  const initialPersistedStripRef = useRef<FeedFiltersState | null | undefined>(undefined);
  if (initialPersistedStripRef.current === undefined) {
    initialPersistedStripRef.current =
      typeof window !== "undefined"
        ? decideRestoredLiveStrip(
            window.sessionStorage.getItem(buildLiveStripStorageKey(currentUserId)),
            Date.now(),
            LIVE_STRIP_TTL_MS
          )
        : null;
  }

  const initialLiveStripStateRef = useRef<ReturnType<typeof decideInitialLiveStripState> | undefined>(undefined);
  if (initialLiveStripStateRef.current === undefined) {
    initialLiveStripStateRef.current = decideInitialLiveStripState(
      initialPersistedStripRef.current,
      initialPrefsCacheRef.current,
      EMPTY_STRIP
    );
  }

  const [strip, setStrip] = useState<FeedFiltersState>(() => initialLiveStripStateRef.current!.strip);
  // Prefs resolution is a two-phase gate: while "pending" the strip still holds
  // the EMPTY_STRIP placeholder (not the user's real preferences), so kicking off
  // the listings fetch now would waste a request against empty filters and then
  // immediately refetch once loadPrefsIntoStrip resolves the real filters. Model
  // the phase explicitly and gate the feed's `enabled` on it. A fresh cache hit
  // (persisted strip OR prefs cache) seeds this as already "resolved" (the strip
  // above is already the real one); `loadPrefsIntoStrip` still runs in the
  // background to revalidate — see `restoredStripFromPersistenceRef` below for
  // why it must not clobber a strip restored from persistence.
  const [prefsPhase, setPrefsPhase] = useState<"pending" | "resolved">(() => initialLiveStripStateRef.current!.phase);
  const prefsResolved = prefsPhase === "resolved";
  // "Restore-wins": when the initial strip came from PERSISTED user edits
  // (back-nav), `loadPrefsIntoStrip`'s background revalidate below must not
  // overwrite it with prefs-derived values — the user should see exactly what
  // they left, not a prefs-cache refresh. The prefs-cache sessionStorage write
  // (in `loadPrefsIntoStrip`'s `finally`) still runs regardless, so a FUTURE
  // mount with no persisted strip still gets a fresh prefs seed.
  //
  // This flag is READ by every mount-time call to `loadPrefsIntoStrip` but
  // NEVER flipped by it (see the comment there for why: Next's App Router
  // defaults `reactStrictMode` on, so `next dev` double-invokes mount
  // effects — an earlier "consume on first read" design was eaten by the
  // FIRST of those two calls, leaving the SECOND call's later-resolving
  // fetch free to clobber the restored strip ~1s after mount). The only
  // thing allowed to flip it back to `false` is a genuine external signal —
  // the `kraahl-user-preferences-changed` handler below, right before it
  // calls `loadPrefsIntoStrip` — because a real prefs edit legitimately
  // should apply. The discriminator is "is this a real prefs change", not
  // "is this the Nth call", which is what makes it StrictMode-safe.
  const restoredStripFromPersistenceRef = useRef(initialLiveStripStateRef.current!.restoredFromPersistence);
  const filtersDragRef = useRef({ active: false, startX: 0, startY: 0 });
  const filtersDraggingRef = useRef(false);

  const { sortBy, ...criteria } = strip;

  const {
    items,
    isLoading,
    isInitialized,
    error,
    hasNextPage,
    loadMore,
    refetchFirstPage,
  } = usePaginatedFeed({
    userId: currentUserId,
    mode: "just-listed",
    filters: criteria as FeedFilterState,
    limit: PAGE_LIMIT,
    maxItems: null,
    sortBy: sortBy ?? null,
    // Hold the first listings fetch until preferences are resolved (loaded OR
    // determined-absent). For logged-out / no-prefs users loadPrefsIntoStrip
    // resolves synchronously, so this gates for at most one render.
    enabled: prefsResolved,
    // Persist + restore the feed across navigation (skeleton-on-back fix). The
    // scroll root is the shared `[data-feed-scroll-root]` container rendered by
    // feed-page-client-{mobile,desktop}. @see lib/feed/feed-snapshot.ts
    cacheKey: "live",
  });

  const refetchFirstPageRef = useRef(refetchFirstPage);
  refetchFirstPageRef.current = refetchFirstPage;
  const loadMoreRef = useRef(loadMore);
  loadMoreRef.current = loadMore;

  const loadPrefsIntoStrip = useCallback(async () => {
    // READ-ONLY here — do NOT flip `restoredStripFromPersistenceRef` in this
    // function. It used to be consumed ("one-shot") on the first call, but
    // React's App Router default `reactStrictMode: true` (see next.config.ts
    // — not overridden) makes `next dev` invoke the mount effect that calls
    // this function TWICE (mount → cleanup → mount). The first invocation
    // consumed the flag; the second then proceeded as if nothing was
    // restored, and its `fetch` resolving ~1s later clobbered the restored
    // strip out from under the user. Reading without flipping makes EVERY
    // mount-time call (1 in prod, 2 in dev under StrictMode) agree: skip the
    // clobber. The flag is only cleared by a genuine external signal — see
    // the `kraahl-user-preferences-changed` handler below.
    const skipClobber = restoredStripFromPersistenceRef.current;
    if (!currentUserId) {
      if (!skipClobber) {
        setStrip({ ...EMPTY_STRIP });
      }
      setPrefsPhase("resolved");
      return;
    }
    let resolvedPartial: Partial<FeedFiltersState> = {};
    try {
      const res = await fetch("/api/user/preferences");
      if (!res.ok) throw new Error("prefs");
      const data = (await res.json()) as { categories: string[] | null; onboarding_completed_at: string | null };
      const keys = normalizePreferenceCategories(data.categories ?? []);
      if (!keys.length || !data.onboarding_completed_at) {
        resolvedPartial = {};
      } else {
        const supabase = createClient();
        resolvedPartial = await collectorKeysToFeedFiltersPartial(supabase, keys);
      }
      if (!skipClobber) {
        setStrip({ ...EMPTY_STRIP, ...resolvedPartial });
      }
    } catch {
      resolvedPartial = {};
      if (!skipClobber) {
        setStrip({ ...EMPTY_STRIP });
      }
    } finally {
      // Cache the resolved partial (best-effort) so the NEXT mount's lazy
      // initializer can seed `strip` synchronously. Accept the key change if
      // the freshly-resolved strip differs from what seeded this render — a
      // genuine prefs change legitimately lands on the hook's filters-changed
      // path (scroll-to-top), same as before this cache existed.
      try {
        window.sessionStorage.setItem(
          buildPrefsStripCacheKey(currentUserId),
          serializePrefsStripCache(resolvedPartial, Date.now())
        );
      } catch {
        /* private mode / quota — cache is best-effort */
      }
      setPrefsPhase("resolved");
    }
  }, [currentUserId]);

  useEffect(() => {
    void loadPrefsIntoStrip();
  }, [loadPrefsIntoStrip]);

  // Persist the strip on every change (best-effort) so a remount after
  // back-nav (initialPersistedStripRef above) restores the user's applied
  // chips — and, transitively, lets usePaginatedFeed's `{cacheKey: "live"}`
  // snapshot key match again. Persisting prefs-derived strips too is fine and
  // intended: restoring them just reproduces what was already on screen.
  // @see lib/feed/live-strip-persistence.ts
  useEffect(() => {
    try {
      window.sessionStorage.setItem(buildLiveStripStorageKey(currentUserId), serializeLiveStrip(strip, Date.now()));
    } catch {
      /* private mode */
    }
  }, [strip, currentUserId]);

  useEffect(() => {
    const onPrefs = () => {
      // A genuine prefs edit happened elsewhere (e.g. onboarding) — this is
      // the ONE place allowed to clear restore-wins: real intent to change
      // filters, as opposed to a mount-time StrictMode double-invoke of
      // `loadPrefsIntoStrip` (see the comment there). Flip BEFORE calling
      // `loadPrefsIntoStrip` below so its read of the flag already reflects
      // "no longer restoring — apply prefs normally".
      restoredStripFromPersistenceRef.current = false;
      // The old cached partial is stale the instant prefs changed elsewhere
      // (e.g. onboarding) — drop it immediately rather than waiting for the
      // refetch below to overwrite it.
      if (currentUserId) {
        try {
          window.sessionStorage.removeItem(buildPrefsStripCacheKey(currentUserId));
        } catch {
          /* private mode */
        }
      }
      void loadPrefsIntoStrip();
    };
    window.addEventListener("kraahl-user-preferences-changed", onPrefs);
    return () => window.removeEventListener("kraahl-user-preferences-changed", onPrefs);
  }, [loadPrefsIntoStrip, currentUserId]);

  // `refreshSignal` (= feed-page-client-state's discoverRefreshKey) only
  // increments on an explicit user tab-switch/re-tap, never on mount — so the
  // very FIRST value this effect observes (whatever it is, restored tab or
  // not) must be treated as "nothing happened yet", not a refresh request.
  // This is what keeps a snapshot-restored back-nav mount from rerolling +
  // scrolling to top on its own.
  useEffect(() => {
    if (typeof refreshSignal === "undefined") return;
    if (!refreshInitializedRef.current) {
      refreshInitializedRef.current = true;
      return;
    }
    const scrollRoot = document.querySelector("[data-feed-scroll-root]") as HTMLElement | null;
    scrollRoot?.scrollTo({ top: 0, behavior: "smooth" });
    refetchFirstPageRef.current();
  }, [refreshSignal]);

  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || !hasNextPage) return;
    const root = node.closest("[data-feed-scroll-root]") as Element | null;
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry) return;
        if (!entry.isIntersecting) {
          loadMoreArmedRef.current = true;
          return;
        }
        if (!loadMoreArmedRef.current) return;
        loadMoreArmedRef.current = false;
        loadMoreRef.current();
      },
      { root: root ?? undefined, rootMargin: "600px", threshold: 0 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasNextPage]);

  // Re-arm after a page lands: IntersectionObserver only fires on an
  // intersection *transition*, so a sentinel still in view after a (short) page
  // appends would otherwise stall until the user scrolls up and back down.
  useEffect(() => {
    loadMoreArmedRef.current = true;
  }, [items.length]);

  const clearFiltersDraggingSoon = useCallback(() => {
    window.setTimeout(() => {
      filtersDraggingRef.current = false;
      filtersDragRef.current.active = false;
    }, 0);
  }, []);

  const handleFiltersPointerDown = useCallback((event: React.PointerEvent) => {
    if (event.pointerType !== "touch") return;
    filtersDragRef.current.active = true;
    filtersDragRef.current.startX = event.clientX ?? 0;
    filtersDragRef.current.startY = event.clientY ?? 0;
    filtersDraggingRef.current = false;
  }, []);

  const handleFiltersPointerMove = useCallback((event: React.PointerEvent) => {
    if (event.pointerType !== "touch") return;
    if (!filtersDragRef.current.active) return;
    if (filtersDraggingRef.current) return;
    const dx = (event.clientX ?? 0) - filtersDragRef.current.startX;
    const dy = (event.clientY ?? 0) - filtersDragRef.current.startY;
    if (Math.abs(dx) > 8 && Math.abs(dx) > Math.abs(dy)) {
      filtersDraggingRef.current = true;
    }
  }, []);

  const handleFiltersPointerUp = useCallback(() => {
    clearFiltersDraggingSoon();
  }, [clearFiltersDraggingSoon]);

  const handleFiltersPointerCancel = useCallback(() => {
    clearFiltersDraggingSoon();
  }, [clearFiltersDraggingSoon]);

  const blockClickIfDragging = useCallback((event: React.MouseEvent | React.PointerEvent) => {
    if (!filtersDraggingRef.current) return;
    event.preventDefault();
    event.stopPropagation();
  }, []);

  const onStripPointerDown = useCallback(
    (e: React.PointerEvent) => {
      handleFiltersPointerDown(e);
    },
    [handleFiltersPointerDown]
  );

  const onStripPointerMove = useCallback(
    (e: React.PointerEvent) => {
      handleFiltersPointerMove(e);
    },
    [handleFiltersPointerMove]
  );

  const onStripPointerUp = useCallback(
    (e: React.PointerEvent) => {
      handleFiltersPointerUp();
    },
    [handleFiltersPointerUp]
  );

  const onStripPointerCancel = useCallback(
    (e: React.PointerEvent) => {
      handleFiltersPointerCancel();
    },
    [handleFiltersPointerCancel]
  );

  const chipActive = useMemo(() => {
    const f = strip;
    return (
      f.listingCategory !== null ||
      (f.categories?.length ?? 0) > 0 ||
      (f.brands?.length ?? 0) > 0 ||
      (f.themes?.length ?? 0) > 0 ||
      (f.tcgs?.length ?? 0) > 0 ||
      (f.conditions?.length ?? 0) > 0 ||
      (f.listingFormats?.length ?? 0) > 0 ||
      f.gradeRange !== null ||
      (f.cardGradeRange ?? null) !== null ||
      (f.tcgSets?.length ?? 0) > 0 ||
      (f.tcgRarities?.length ?? 0) > 0 ||
      (f.cardConditions?.length ?? 0) > 0 ||
      (f.cardLanguages?.length ?? 0) > 0 ||
      (f.cardFormats?.length ?? 0) > 0 ||
      (f.gradingCompanies?.length ?? 0) > 0 ||
      (f.mtgPrintVariants?.length ?? 0) > 0
    );
  }, [strip]);

  // While prefs are still pending the feed is disabled (no fetch yet, so
  // isLoading is false) — keep the skeleton up rather than flashing an empty
  // grid until the first prefs-applied fetch begins.
  const isInitialLoading = !prefsResolved || (!isInitialized && isLoading);
  const isLoadingMore = isInitialized && isLoading;
  const trailingCount = items.length % columns;
  const fillerCount = trailingCount === 0 ? 0 : columns - trailingCount;
  const showEmptyState = isInitialized && !isLoading && items.length === 0 && !error;

  return (
    <div className="w-full">
      {prefsResolved ? (
        <div
          className="scrollbar-hide mb-4 overflow-x-auto overflow-y-visible overscroll-x-contain touch-pan-x [-webkit-overflow-scrolling:touch]"
          onPointerDown={onStripPointerDown}
          onPointerMove={onStripPointerMove}
          onPointerUp={onStripPointerUp}
          onPointerCancel={onStripPointerCancel}
        >
          <div className="flex w-max min-w-full gap-2 p-0.5">
            <Button
              type="button"
              variant={chipActive ? "outline-solid" : "default"}
              size="sm"
              className={cn(
                "h-8 shrink-0 rounded-full border px-3 text-xs font-medium shadow-sm",
                !chipActive && "ring-2 ring-primary/40"
              )}
              onClickCapture={blockClickIfDragging}
              onClick={() => {
                setStrip((prev) => ({ ...EMPTY_STRIP, sortBy: prev.sortBy }));
              }}
            >
              {tFilters("all")}
            </Button>
            <div className="flex shrink-0 items-center gap-2" onClickCapture={blockClickIfDragging}>
              <FeedFilters
                chipRow
                stripDragRef={filtersDraggingRef}
                inline
                open
                onOpenChangeAction={() => {}}
                value={strip}
                onChange={setStrip}
                renderButton={() => null}
                activeTab="live"
              />
            </div>
          </div>
        </div>
      ) : null}

      {error && (
        <div className="py-8 text-center">
          <div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-destructive/10 text-destructive">
            <AlertTriangle className="h-5 w-5" />
          </div>
          <p className="mb-4 text-sm text-muted-foreground">Oops, an error occurred.</p>
          <button
            type="button"
            onClick={() => refetchFirstPage()}
            className="rounded-full bg-primary px-6 py-3 font-medium text-primary-foreground transition-colors duration-200 hover:bg-primary/90"
          >
            Retry
          </button>
        </div>
      )}

      {isInitialLoading && (
        <ItemCardSkeletonGrid
          count={12}
          keyPrefix="live-feed-initial"
          gridClassName="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-4 md:gap-x-5 gap-y-4"
        />
      )}

      {!error && items.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-4 md:gap-x-5 gap-y-4">
          {items.map((listing) => (
            // feed-card-cv: content-visibility virtualization-lite for the
            // long infinite-scroll grid — live grid only, not snap feeds.
            // @see app/globals.css
            <div key={listing.id} className="h-auto min-w-0 feed-card-cv">
              <ItemCard
                listingId={listing.id}
                title={listing.title}
                price={listing.price}
                currency={listing.currency ?? null}
                image_urls={listing.image_urls}
                stock={listing.quantity}
                scheduledPublishAt={listing.scheduled_publish_at as string}
                listingFormat={listing.listing_format ?? null}
                listingType={listing.type ?? null}
                auctionEndAt={listing.auction_end_at ?? null}
                auctionStatus={listing.auction_status ?? null}
                auctionBidCount={listing.auction_bid_count ?? null}
                offersPendingCount={listing.offers_pending_count ?? null}
                initialSaved={listing.initialSaved}
                savesCount={listing.saves_count}
                currentUserId={currentUserId}
                sellerId={listing.user_id}
                sellerName={listing.sellerUsername}
                sellerAvatarUrl={listing.sellerAvatarUrl ?? null}
                condition={listing.condition}
                cardDetails={listing.card_details ?? null}
                description={listing.description}
                onClick={() => router.push(`/listings/${listing.id}`)}
              />
            </div>
          ))}
          {isLoadingMore && fillerCount > 0 ? (
            <ItemCardSkeletonGrid count={fillerCount} keyPrefix="live-feed-filler" inline />
          ) : null}
          {isLoadingMore ? (
            <ItemCardSkeletonGrid count={columns} keyPrefix="live-feed-loading" inline />
          ) : null}
        </div>
      )}

      {showEmptyState && (
        <div className="flex min-h-[50vh] items-center justify-center">
          {/* EMPTY_STATES.md: category filter engaged → that category's
              cutout + sell CTA (auth-gated); otherwise the generic copy. */}
          <EmptyState
            {...(strip.listingCategory
              ? {
                  photoSlug: CATEGORY_PHOTO_IDS.has(
                    CATEGORY_AXIS_TO_SLUG[strip.listingCategory],
                  )
                    ? CATEGORY_AXIS_TO_SLUG[strip.listingCategory]
                    : undefined,
                  title: tEmpty("categoryTitle"),
                  description: tEmpty("categoryDesc"),
                  action: {
                    label: tEmpty("sellCta"),
                    foil: true,
                    onClick: () => {
                      if (currentUserId) openPanel({ kind: "creation" });
                      else setSellAuthOpen(true);
                    },
                  },
                }
              : {
                  title: tEmpty("noListingsTitle"),
                  description: tEmpty("noListingsDesc"),
                })}
          />
        </div>
      )}
      {!currentUserId && (
        <AuthModal isOpen={sellAuthOpen} onCloseAction={() => setSellAuthOpen(false)} />
      )}

      <div className="flex min-h-16 justify-center py-6">
        {isLoadingMore && items.length > 0 ? <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" /> : null}
      </div>
      <div ref={sentinelRef} className="h-px w-full shrink-0" aria-hidden />
    </div>
  );
}
